import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AudioSyncService {
  // Recording state observable
  private isRecordingSubject = new BehaviorSubject<boolean>(false);
  isRecording$ = this.isRecordingSubject.asObservable();

  // Timer observable
  private timerSubject = new BehaviorSubject<string>('00:00:00');
  timer$ = this.timerSubject.asObservable();

  // Emitted once recording is stopped
  private recordingStopped$ = new BehaviorSubject<Blob | null>(null);
  recordingStoppedObs$ = this.recordingStopped$.asObservable();

  // Internal state
  private timerSubscription: Subscription | null = null;
  private seconds = 0;
  private mediaRecorder!: MediaRecorder;
  private stream: MediaStream | null = null;
  private recordedChunks: Blob[] = [];

  // Flag to make sure only one component takes ownership of recording logic
  private recordingOwnerSet = false;

  // ======= Public API =======

  async startMediaRecorder(): Promise<void> {
    this.recordedChunks = [];

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(this.stream);

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) this.recordedChunks.push(event.data);
      };

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, { type: 'audio/webm' });
        this.recordingStopped$.next(blob);
        this.stopTimer();
      };

      this.mediaRecorder.start();
      this.setRecordingState(true);
      this.startTimer();
    } catch (err) {
      console.error('Microphone access error:', err);
      this.setRecordingState(false);
    }
  }

  stopMediaRecorder(): void {
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.stop();
      this.setRecordingState(false);
      this.stream?.getTracks().forEach((track) => track.stop());
    }
  }

  setRecordingState(state: boolean): void {
    this.isRecordingSubject.next(state);

    // Reset ownership on stop
    if (!state) {
      this.recordingOwnerSet = false;
    }
  }

  get currentRecordingState(): boolean {
    return this.isRecordingSubject.value;
  }

  /**
   * Allows only one component to handle:
   * - socket streaming
   * - task creation
   * - recording processing
   */
  canTakeOwnership(): boolean {
    if (!this.recordingOwnerSet) {
      this.recordingOwnerSet = true;
      return true;
    }
    return false;
  }

  // ======= Timer Handling =======

  private startTimer(): void {
    this.seconds = 0;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.seconds++;
      const hrs = String(Math.floor(this.seconds / 3600)).padStart(2, '0');
      const mins = String(Math.floor((this.seconds % 3600) / 60)).padStart(2, '0');
      const secs = String(this.seconds % 60).padStart(2, '0');
      this.timerSubject.next(`${hrs}:${mins}:${secs}`);
    });
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
    this.timerSubject.next('00:00:00');
  }
}
